import { computed, ref, Ref } from "@vue/composition-api";
import { FileUploadResponse } from "@scrile/api-provider/dist/api/FileProvider";
import { VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { getFileMimeType } from "@/lib/fileHelpers";
import FileUploader, { calculateProgress } from "@/lib/FileUploader";
import { hasHeicFiles, prepareFilesForUpload } from "@/lib/heicHelpers";
import { UploadVaultFile } from "@/types";

// backend limit of /vaults/files/upload-urls per request
const UPLOAD_URLS_PER_REQUEST = 100;
// how many files of the folder are checked for an already uploaded copy before the upload starts
const DUPLICATE_CHECK_LIMIT = 1000;

export interface VaultUploadHandlers {
  // the file is uploaded and registered in the vault, the tile with uploadUuid must be replaced by vaultFile
  onSuccess: (uploadUuid: string, vaultFile: VaultFile) => void;
  // the same file (name and size) is already in the folder, the tile with uploadUuid must be removed
  onDuplicate: (uploadUuid: string, existingFile: VaultFile) => void;
  onProgress: (file: UploadVaultFile, uploadProgress: number) => void;
  onError: (file: UploadVaultFile) => void;
  // the tile with oldUuid is uploaded again under a new upload url
  onRetry: (oldUuid: string, file: UploadVaultFile) => void;
}

export interface VaultUploadStatus {
  // files of the current batch: queued, uploading or failed
  total: number;
  uploaded: number;
  skipped: number;
  failed: number;
}

interface PendingUpload {
  file: File;
  categoryId: string;
}

// natural order: billie_set03_9 before billie_set03_10
export const compareFileNames = (a: File, b: File) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });

export const getFileKey = (name?: string, size?: string | number) => `${name ?? ""}|${size ?? ""}`;

export const useVaultFiles = (selectedCategoryId: Ref<string>) => {
  const { authUserStrict } = useAuthUser();

  const moveLoading = ref(false);
  const deleteLoading = ref(false);
  const filesToUploadCount = ref(0);
  const failedUuids = ref<string[]>([]);
  const uploadStatus = ref<VaultUploadStatus>({ total: 0, uploaded: 0, skipped: 0, failed: 0 });
  // kept until the file is uploaded so a failed file can be uploaded again
  const pendingUploads = new Map<string, PendingUpload>();

  const uploadingFiles = computed(() => filesToUploadCount.value > 0);

  const updateStatus = (patch: Partial<VaultUploadStatus>) => {
    uploadStatus.value = { ...uploadStatus.value, ...patch, failed: failedUuids.value.length };
  };

  const moveFiles = async (fileIds: string[], categoryId: string) => {
    try {
      moveLoading.value = true;
      return await providers.VaultsProvider.vaultFilesMove({
        data: { userId: authUserStrict.value.id, ids: fileIds, destCategoryId: categoryId },
      });
    } finally {
      moveLoading.value = false;
    }
  };

  const deleteFiles = async (fileIds: string[]): Promise<boolean> => {
    try {
      deleteLoading.value = true;
      return await providers.VaultsProvider.deleteFileFromCategory({
        data: { ids: fileIds, categoryId: selectedCategoryId.value },
      });
    } finally {
      deleteLoading.value = false;
    }
  };

  const getUploadUrls = async (count: number): Promise<FileUploadResponse[]> => {
    const result: FileUploadResponse[] = [];
    for (let i = 0; i < count; i += UPLOAD_URLS_PER_REQUEST) {
      result.push(...(await providers.VaultsProvider.getUploadUrls(Math.min(UPLOAD_URLS_PER_REQUEST, count - i))));
    }
    return result;
  };

  // names and sizes of the files already stored in the folder
  const getExistingFileKeys = async (categoryId: string): Promise<Set<string>> => {
    try {
      const response = await providers.VaultsProvider.search(
        {
          data: {
            userId: authUserStrict.value.id,
            categoryIds: [categoryId],
            page: 0,
            size: DUPLICATE_CHECK_LIMIT,
          },
        },
        ["uuid", "metadata"]
      );
      return new Set(
        (response?.result ?? [])
          .filter(f => f.metadata?.filename)
          .map(f => getFileKey(f.metadata.filename, f.metadata.contentLength))
      );
    } catch (e) {
      // the backend skips duplicates anyway, the check only saves the upload time
      return new Set();
    }
  };

  const createTile = (upload: FileUploadResponse, file: File, categoryId: string): UploadVaultFile => ({
    id: upload.uuid,
    userId: authUserStrict.value.id,
    uuid: upload.uuid,
    contentType: getFileMimeType(file),
    categoryIds: [categoryId],
    urlPart: "",
    createTime: "",
    lastUseTime: "",
    metadata: { filename: file.name, contentLength: String(file.size) },
    uploadProgress: 0,
  });

  const markFailed = (tile: UploadVaultFile, handlers: VaultUploadHandlers) => {
    if (!failedUuids.value.includes(tile.uuid)) failedUuids.value = [...failedUuids.value, tile.uuid];
    filesToUploadCount.value -= 1;
    updateStatus({});
    handlers.onError({ ...tile, uploadError: true });
  };

  const toUploadData = (
    tile: UploadVaultFile,
    upload: FileUploadResponse,
    pending: PendingUpload,
    handlers: VaultUploadHandlers
  ): UploadFileData => ({
    file: pending.file,
    fileType: getFileMimeType(pending.file),
    urlPart: upload.urlPart,
    id: upload.uuid,
    onSuccess: async () => {
      try {
        const vaultFile = await providers.VaultsProvider.createFile({
          data: { userId: authUserStrict.value.id, uuid: upload.uuid, categoryId: pending.categoryId },
        });
        pendingUploads.delete(upload.uuid);
        filesToUploadCount.value -= 1;
        if (vaultFile.uuid !== upload.uuid) {
          // the backend found the same file in the folder and returned it instead of creating a duplicate
          updateStatus({ skipped: uploadStatus.value.skipped + 1 });
          handlers.onDuplicate(upload.uuid, vaultFile);
        } else {
          updateStatus({ uploaded: uploadStatus.value.uploaded + 1 });
          handlers.onSuccess(upload.uuid, vaultFile);
        }
      } catch (e) {
        markFailed(tile, handlers);
      }
    },
    onUploadProgress: p => {
      handlers.onProgress(tile, calculateProgress(p));
    },
    onError: () => markFailed(tile, handlers),
  });

  const addFiles = async (
    selectedFiles: FileList | File[],
    handlers: VaultUploadHandlers
  ): Promise<UploadVaultFile[]> => {
    // the folder is fixed when the upload starts, the user may open another folder while files are uploading
    const categoryId = selectedCategoryId.value;
    if (!categoryId) return [];
    // HEIC can't be processed by the server converter: convert it to JPEG here or reject it before upload
    const files = hasHeicFiles(selectedFiles) ? (await prepareFilesForUpload(selectedFiles)).files : selectedFiles;
    if (!files.length) return [];

    if (!uploadingFiles.value) {
      uploadStatus.value = { total: 0, uploaded: 0, skipped: 0, failed: failedUuids.value.length };
    }

    const existingKeys = await getExistingFileKeys(categoryId);
    const selectedKeys = new Set<string>();
    const filesToUpload = Array.from(files)
      .filter(file => {
        const key = getFileKey(file.name, file.size);
        if (existingKeys.has(key) || selectedKeys.has(key)) return false;
        selectedKeys.add(key);
        return true;
      })
      // the picker gives files in its own order (iOS Files: by date or by tap), a photo set is numbered by name
      .sort(compareFileNames);
    const skipped = files.length - filesToUpload.length;
    updateStatus({ skipped: uploadStatus.value.skipped + skipped });
    if (!filesToUpload.length) return [];

    const uploadUrls = await getUploadUrls(filesToUpload.length);
    const tiles: UploadVaultFile[] = [];
    const uploadData: UploadFileData[] = [];
    filesToUpload.forEach((file, index) => {
      const upload = uploadUrls[index];
      const pending = { file, categoryId };
      const tile = createTile(upload, file, categoryId);
      pendingUploads.set(upload.uuid, pending);
      tiles.push(tile);
      uploadData.push(toUploadData(tile, upload, pending, handlers));
    });

    filesToUploadCount.value += uploadData.length;
    updateStatus({ total: uploadStatus.value.total + uploadData.length });
    FileUploader.addToQueue(uploadData);
    return tiles;
  };

  const retryFile = async (uuid: string, handlers: VaultUploadHandlers) => {
    const pending = pendingUploads.get(uuid);
    if (!pending || !failedUuids.value.includes(uuid)) return;
    failedUuids.value = failedUuids.value.filter(i => i !== uuid);
    filesToUploadCount.value += 1;
    updateStatus({});
    try {
      // a new upload url: the previous one may be expired or partially written
      const [upload] = await providers.VaultsProvider.getUploadUrls(1);
      pendingUploads.delete(uuid);
      pendingUploads.set(upload.uuid, pending);
      const tile = createTile(upload, pending.file, pending.categoryId);
      handlers.onRetry(uuid, tile);
      FileUploader.addToQueue([toUploadData(tile, upload, pending, handlers)]);
    } catch (e) {
      failedUuids.value = [...failedUuids.value, uuid];
      filesToUploadCount.value -= 1;
      updateStatus({});
    }
  };

  const retryFailed = (handlers: VaultUploadHandlers) =>
    Promise.all(failedUuids.value.map(uuid => retryFile(uuid, handlers)));

  // forget failed files of another folder, their tiles are not shown anymore
  const forgetFailed = () => {
    failedUuids.value.forEach(uuid => pendingUploads.delete(uuid));
    failedUuids.value = [];
    updateStatus({});
  };

  return {
    moveLoading,
    deleteLoading,
    uploadingFiles,
    uploadStatus,
    moveFiles,
    deleteFiles,
    addFiles,
    retryFile,
    retryFailed,
    forgetFailed,
  };
};
